import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ContextView } from '@instructure/ui-view';
import { controllable } from '@instructure/ui-prop-types';
import { FormField, FormPropTypes } from '@instructure/ui-form-field';
import { addEventListener } from '@instructure/ui-dom-utils';
import { uid } from '@instructure/uid';
import { themeable } from '@instructure/ui-themeable';
import { testable } from '@instructure/ui-testable';
import { omitProps, pickProps } from '@instructure/ui-react-utils';
import { isEdge } from '@instructure/ui-utils';
var styles = {
  componentId: 'dSsWD',
  template: function template(theme) {
    return "\n\n.dSsWD_bGBk{align-items:center;display:flex;justify-content:center;min-width:".concat(theme.minWidth || 'inherit', "}\n\n[dir=rtl] .dSsWD_cwos{direction:rtl}\n\n.dSsWD_cwos{-moz-appearance:none;-webkit-appearance:none;all:initial;animation:none 0s ease 0s 1 normal none running;appearance:none;backface-visibility:visible;background:transparent none repeat 0 0/auto auto padding-box border-box scroll;border:medium none currentColor;border-collapse:separate;border-image:none;border-radius:0;border-spacing:0;bottom:auto;box-shadow:none;box-sizing:content-box;box-sizing:border-box;caption-side:top;clear:none;clip:auto;color:#000;column-count:auto;column-fill:balance;column-gap:normal;column-rule:medium none currentColor;column-span:1;column-width:auto;columns:auto;content:normal;counter-increment:none;counter-reset:none;cursor:auto;direction:ltr;display:inline;empty-cells:show;flex:auto;float:none;font-family:serif;font-size:medium;font-stretch:normal;font-style:normal;font-variant:normal;font-weight:400;height:auto;hyphens:none;left:auto;letter-spacing:normal;line-height:normal;list-style:disc outside none;margin:0;max-height:none;max-width:none;min-height:0;min-width:0;opacity:1;orphans:2;outline:medium none invert;outline:none;overflow:visible;overflow-x:visible;overflow-y:visible;padding:0;page-break-after:auto;page-break-before:auto;page-break-inside:auto;perspective:none;perspective-origin:50% 50%;position:static;right:auto;tab-size:8;table-layout:auto;text-align:left;text-align-last:auto;text-decoration:none;text-indent:0;text-shadow:none;text-transform:none;top:auto;transform:none;transform-origin:50% 50% 0;transform-style:flat;transition:none 0s ease 0s;unicode-bidi:normal;vertical-align:baseline;visibility:visible;white-space:normal;widows:2;width:auto;width:100%;word-spacing:normal;z-index:auto}\n\n.dSsWD_cwos::-moz-range-thumb{-moz-appearance:none;appearance:none;background:").concat(theme.handleBackground || 'inherit', ";border-radius:50%;box-shadow:0 0.0625rem 0 ").concat(theme.handleShadowColor || 'inherit', ";cursor:pointer;height:").concat(theme.handleSize || 'inherit', ";transition:all 0.15s ease-in-out;width:").concat(theme.handleSize || 'inherit', "}\n\n.dSsWD_cwos::-moz-range-thumb:hover{background:").concat(theme.handleHoverBackground || 'inherit', "}\n\n.dSsWD_cwos::-ms-thumb{appearance:none;background:").concat(theme.handleBackground || 'inherit', ";border-radius:50%;box-shadow:0 0.0625rem 0 ").concat(theme.handleShadowColor || 'inherit', ";cursor:pointer;height:").concat(theme.handleSize || 'inherit', ";transition:all 0.15s ease-in-out;width:").concat(theme.handleSize || 'inherit', "}\n\n.dSsWD_cwos::-ms-thumb:hover{background:").concat(theme.handleHoverBackground || 'inherit', "}\n\n.dSsWD_cwos::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;background:").concat(theme.handleBackground || 'inherit', ";border-radius:50%;box-shadow:0 0.0625rem 0 ").concat(theme.handleShadowColor || 'inherit', ";cursor:pointer;height:").concat(theme.handleSize || 'inherit', ";transition:all 0.15s ease-in-out;width:").concat(theme.handleSize || 'inherit', "}\n\n.dSsWD_cwos::-webkit-slider-thumb:hover{background:").concat(theme.handleHoverBackground || 'inherit', "}\n\n.dSsWD_cwos::-webkit-slider-thumb{margin-top:calc(-1*").concat(theme.handleSize || 'inherit', "/4)}\n\n.dSsWD_cwos:active,.dSsWD_cwos:focus{outline:none}\n\n.dSsWD_cwos:active::-moz-range-thumb,.dSsWD_cwos:focus::-moz-range-thumb{box-shadow:0 0.0625rem 0 ").concat(theme.handleShadowColor || 'inherit', ",0 0 0 ").concat(theme.handleFocusOutlineWidth || 'inherit', " ").concat(theme.handleFocusOutlineColor || 'inherit', "}\n\n.dSsWD_cwos:active::-ms-thumb,.dSsWD_cwos:focus::-ms-thumb{box-shadow:0 0.0625rem 0 ").concat(theme.handleShadowColor || 'inherit', ",0 0 0 ").concat(theme.handleFocusOutlineWidth || 'inherit', " ").concat(theme.handleFocusOutlineColor || 'inherit', "}\n\n.dSsWD_cwos:active::-webkit-slider-thumb,.dSsWD_cwos:focus::-webkit-slider-thumb{box-shadow:0 0.0625rem 0 ").concat(theme.handleShadowColor || 'inherit', ",0 0 0 ").concat(theme.handleFocusOutlineWidth || 'inherit', " ").concat(theme.handleFocusOutlineColor || 'inherit', "}\n\n.dSsWD_cwos::-moz-focus-inner,.dSsWD_cwos::-moz-focus-outer{border:0;outline:none}\n\n.dSsWD_cwos::-moz-range-track{background:").concat(theme.trackBackground || 'inherit', ";border-color:transparent;border-radius:0.312em;color:transparent;cursor:pointer;height:calc(").concat(theme.handleSize || 'inherit', "/2)}\n\n.dSsWD_cwos::-ms-track{background:").concat(theme.trackBackground || 'inherit', ";border-color:transparent;border-radius:0.312em;color:transparent;cursor:pointer;height:calc(").concat(theme.handleSize || 'inherit', "/2)}\n\n.dSsWD_cwos::-webkit-slider-runnable-track{background:").concat(theme.trackBackground || 'inherit', ";border-color:transparent;border-radius:0.312em;color:transparent;cursor:pointer;height:calc(").concat(theme.handleSize || 'inherit', "/2)}\n\n.dSsWD_cwos::-ms-track{border-width:1rem 0}\n\n.dSsWD_cwos::-ms-fill-lower,.dSsWD_cwos::-ms-fill-upper{background:#2d3b45;border-radius:0.75rem;border-width:1.5rem 0}\n\n.dSsWD_cwos::-ms-tooltip{display:none}\n\n.dSsWD_cwos::-ms-thumb{-webkit-appearance:none;background:#fff;border:0.25rem solid #2d3b45;border-radius:100%;box-sizing:border-box;height:1.75rem;margin-top:0.0625rem;width:1.75rem}\n\n.dSsWD_fvkP .dSsWD_cwos:focus{outline:0.125rem solid ").concat(theme.handleBackground || 'inherit', "}\n\n.dSsWD_fqzO{all:initial;animation:none 0s ease 0s 1 normal none running;backface-visibility:visible;background:transparent none repeat 0 0/auto auto padding-box border-box scroll;border:medium none currentColor;border-collapse:separate;border-image:none;border-radius:0;border-spacing:0;bottom:auto;box-shadow:none;box-sizing:content-box;box-sizing:border-box;caption-side:top;clear:none;clip:auto;color:#000;color:inherit;column-count:auto;column-fill:balance;column-gap:normal;column-rule:medium none currentColor;column-span:1;column-width:auto;columns:auto;content:normal;counter-increment:none;counter-reset:none;cursor:auto;direction:ltr;display:inline;display:block;empty-cells:show;float:none;font-family:serif;font-family:").concat(theme.valueFontFamily || 'inherit', ";font-size:medium;font-stretch:normal;font-style:normal;font-variant:normal;font-weight:400;font-weight:").concat(theme.valueFontWeight || 'inherit', ";height:auto;hyphens:none;left:auto;letter-spacing:normal;line-height:normal;list-style:disc outside none;margin:0;max-height:none;max-width:none;min-height:0;min-width:0;opacity:1;orphans:2;outline:medium none invert;overflow:visible;overflow-x:visible;overflow-y:visible;padding:0;page-break-after:auto;page-break-before:auto;page-break-inside:auto;perspective:none;perspective-origin:50% 50%;position:static;right:auto;tab-size:8;table-layout:auto;text-align:left;text-align:center;text-align-last:auto;text-decoration:none;text-indent:0;text-shadow:none;text-transform:none;top:auto;transform:none;transform-origin:50% 50% 0;transform-style:flat;transition:none 0s ease 0s;unicode-bidi:normal;vertical-align:baseline;visibility:visible;white-space:normal;widows:2;width:auto;word-spacing:normal;z-index:auto}\n\n[dir=ltr] .dSsWD_fqzO,[dir=rtl] .dSsWD_fqzO{text-align:center}\n\n.dSsWD_doqw .dSsWD_fqzO{font-size:").concat(theme.valueSmallFontSize || 'inherit', ";line-height:").concat(theme.valueSmallLineHeight || 'inherit', ";padding:").concat(theme.valueSmallPadding || 'inherit', "}\n\n.dSsWD_ycrn .dSsWD_fqzO{font-size:").concat(theme.valueMediumFontSize || 'inherit', ";line-height:").concat(theme.valueMediumLineHeight || 'inherit', ";padding:").concat(theme.valueMediumPadding || 'inherit', "}\n\n.dSsWD_cMDj .dSsWD_fqzO{font-size:").concat(theme.valueLargeFontSize || 'inherit', ";line-height:").concat(theme.valueLargeLineHeight || 'inherit', ";padding:").concat(theme.valueLargePadding || 'inherit', "}");
  },
  'root': 'dSsWD_bGBk',
  'input': 'dSsWD_cwos',
  'edge16Up': 'dSsWD_fvkP',
  'value': 'dSsWD_fqzO',
  'small': 'dSsWD_doqw',
  'medium': 'dSsWD_ycrn',
  'large': 'dSsWD_cMDj'
};
import theme from './theme';
/**
---
category: components
---
**/

var RangeInput = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(RangeInput, _Component);

  var _super = _createSuper(RangeInput);

  function RangeInput(props) {
    var _this;

    _classCallCheck(this, RangeInput);

    _this = _super.call(this);

    _this.handleChange = function (event) {
      var _this$props = _this.props,
          onChange = _this$props.onChange,
          value = _this$props.value;

      if (typeof value === 'undefined') {
        _this.setState({
          value: event.target.value
        });
      }

      if (typeof onChange === 'function') {
        onChange(event.target.value);
      }
    };

    _this.noopChange = function () {};

    if (typeof props.value === 'undefined') {
      _this.state = {
        value: props.defaultValue
      };
    }

    _this.defaultId = uid('RangeInput');
    return _this;
  }
  /* workaround for https://github.com/facebook/react/issues/554 */


  _createClass(RangeInput, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      if (!this._input) {
        return;
      } // https://connect.microsoft.com/IE/Feedback/Details/856998


      this.inputListener = addEventListener(this._input, 'input', this.handleChange);
      this.changeListener = addEventListener(this._input, 'change', this.handleChange);
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      if (!this._input) {
        return;
      }

      this.inputListener.remove();
      this.changeListener.remove();
    }
    /* end workaround */

  }, {
    key: "renderValue",
    value: function renderValue() {
      if (this.props.displayValue) {
        return /*#__PURE__*/React.createElement(ContextView, {
          background: "inverse",
          placement: "end center"
        }, /*#__PURE__*/React.createElement("output", {
          htmlFor: this.id,
          className: styles.value
        }, this.props.formatValue(this.value)));
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _classes,
          _this2 = this;

      var _this$props2 = this.props,
          formatValue = _this$props2.formatValue,
          size = _this$props2.size,
          disabled = _this$props2.disabled,
          readOnly = _this$props2.readOnly;
      var props = omitProps(this.props, RangeInput.propTypes);
      var classes = (_classes = {}, _defineProperty(_classes, styles.root, true), _defineProperty(_classes, styles[size], size), _defineProperty(_classes, styles.edge16Up, isEdge), _classes);
      /* eslint-disable jsx-a11y/no-redundant-roles */

      return /*#__PURE__*/React.createElement(FormField, Object.assign({}, pickProps(this.props, FormField.propTypes), {
        id: this.id
      }), /*#__PURE__*/React.createElement("div", {
        className: classnames(classes)
      }, /*#__PURE__*/React.createElement("input", Object.assign({
        className: styles.input,
        ref: function ref(c) {
          _this2._input = c;
        },
        type: "range",
        role: "slider",
        id: this.id,
        min: this.props.min,
        max: this.props.max,
        step: this.props.step,
        value: this.value,
        onChange: this.noopChange,
        "aria-valuenow": this.value,
        "aria-valuemin": this.props.min,
        "aria-valuemax": this.props.max,
        "aria-valuetext": formatValue(this.value, this.props.max)
      }, props, {
        disabled: disabled || readOnly,
        "aria-disabled": disabled || readOnly ? 'true' : null
      })), this.renderValue()));
      /* eslint-enable jsx-a11y/no-redundant-roles */
    }
  }, {
    key: "value",
    get: function get() {
      return typeof this.props.value === 'undefined' ? this.state.value : this.props.value;
    }
  }, {
    key: "invalid",
    get: function get() {
      return this.props.messages && this.props.messages.findIndex(function (message) {
        return message.type === 'error';
      }) >= 0;
    }
  }, {
    key: "id",
    get: function get() {
      return this.props.id || this.defaultId;
    }
  }]);

  RangeInput.displayName = "RangeInput";
  return RangeInput;
}(Component), _class2.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,

  /**
  * value to set on initial render
  */
  defaultValue: PropTypes.number,

  /**
  * the selected value (must be accompanied by an `onChange` prop)
  */
  value: controllable(PropTypes.number),

  /**
  * when used with the `value` prop, the component will not control its own state
  */
  onChange: PropTypes.func,
  messages: PropTypes.arrayOf(FormPropTypes.message),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  layout: PropTypes.oneOf(['stacked', 'inline']),
  id: PropTypes.string,
  label: PropTypes.node.isRequired,

  /**
  * whether to display the current value
  */
  displayValue: PropTypes.bool,
  step: PropTypes.number,

  /**
  * A function to format the displayed value
  */
  formatValue: PropTypes.func,
  inline: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool
}, _class2.defaultProps = {
  step: 1,
  formatValue: function formatValue(val) {
    return val;
  },
  max: 0,
  min: 0,
  inline: false,
  size: 'medium',
  layout: 'stacked',
  displayValue: true,
  disabled: false,
  readOnly: false,
  id: void 0,
  defaultValue: void 0,
  value: void 0,
  onChange: void 0,
  messages: void 0
}, _temp)) || _class) || _class);
export default RangeInput;
export { RangeInput };